import type { MailDailyPoint } from "@feedback-hub/contract/browser";

/** Smallest of 1,2,5 x 10^k that is >= max (min 1). niceCeiling(0)=1, (3)=5, (7)=10, (44)=50. */
export function niceCeiling(max: number): number {
  if (!Number.isFinite(max) || max <= 1) return 1;
  const exp = Math.floor(Math.log10(max));
  const base = 10 ** exp;
  for (const m of [1, 2, 5, 10]) {
    if (m * base >= max) return m * base;
  }
  return 10 * base;
}

/** Ticks 0..ceiling in 4 or 5 whole-number steps when the ceiling divides; else [0, ceiling]. */
export function axisTicks(ceiling: number): number[] {
  for (const steps of [4, 5]) {
    if (ceiling >= steps && ceiling % steps === 0) {
      const step = ceiling / steps;
      return Array.from({ length: steps + 1 }, (_, i) => i * step);
    }
  }
  return [0, ceiling];
}

export interface SendBar {
  day: string;
  recorded: number;
  unattributed: number;
  recordedH: number;
  unattributedH: number;
}

export function sendBars(points: MailDailyPoint[], plotH: number): { bars: SendBar[]; ceiling: number } {
  const rows = points.map((p) => ({ day: p.day, recorded: p.sends, unattributed: p.unattributed ?? 0 }));
  const ceiling = niceCeiling(Math.max(0, ...rows.map((r) => r.recorded + r.unattributed)));
  const bars = rows.map((r) => ({
    ...r,
    recordedH: (r.recorded / ceiling) * plotH,
    unattributedH: (r.unattributed / ceiling) * plotH,
  }));
  return { bars, ceiling };
}

export interface ProblemBar {
  day: string;
  bounces: number;
  complaints: number;
  bouncesH: number;
  complaintsH: number;
}

export function problemBars(points: MailDailyPoint[], plotH: number): { bars: ProblemBar[]; ceiling: number } {
  const ceiling = niceCeiling(Math.max(0, ...points.map((p) => Math.max(p.bounces, p.complaints))));
  const bars = points.map((p) => ({
    day: p.day,
    bounces: p.bounces,
    complaints: p.complaints,
    bouncesH: (p.bounces / ceiling) * plotH,
    complaintsH: (p.complaints / ceiling) * plotH,
  }));
  return { bars, ceiling };
}

/** "Oct 24" for "2026-10-24". A CalendarDate (D10): formatted with timeZone "UTC" so no viewer sees the day before. */
export function dayTickLabel(day: string, locale?: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  return d.toLocaleDateString(locale, { month: "short", day: "numeric", timeZone: "UTC" });
}
